import type { InfiniteData } from '@tanstack/react-query';
import type { FlowRunNode, FlowRunSummary, FlowRunSummaryPage } from '@reflex/flows';

/**
 * Applies a `flow_run:node_updated` payload to a cached flow-runs list,
 * keeping each run's `currentNodeIds` (and the "active nodes" count derived
 * from it) in sync without a refetch.
 *
 * A node is active while `running` or `waiting_approval`; any other status
 * (`pending`, `completed`, `error`, `skipped`) means it is not currently
 * executing. This mirrors how the engine maintains `currentNodeIds` and
 * converges per event from the node's own status.
 *
 * Returns the original array unchanged when the run is absent or the count
 * already reflects the node, so React Query skips an unnecessary re-render.
 */
export function applyNodeUpdateToRunList(
  runs: FlowRunSummary[] | undefined,
  runId: string,
  node: Pick<FlowRunNode, 'nodeId' | 'status'>,
): FlowRunSummary[] | undefined {
  if (!runs) return runs;
  const index = runs.findIndex((run) => run.id === runId);
  if (index === -1) return runs;

  const run = runs[index];
  const isActive = node.status === 'running' || node.status === 'waiting_approval';
  const alreadyListed = run.currentNodeIds.includes(node.nodeId);
  if (isActive === alreadyListed) return runs;

  const currentNodeIds = isActive
    ? [...run.currentNodeIds, node.nodeId]
    : run.currentNodeIds.filter((id) => id !== node.nodeId);
  const next = runs.slice();
  next[index] = { ...run, currentNodeIds };
  return next;
}

/**
 * Narrow a cache entry to a flow-runs list. `setQueriesData` with the
 * `['flow-runs', org]` prefix also matches the active-runs snapshot cache
 * (`['flow-runs', org, 'active']`), whose entries are `ActiveFlowRunSnapshot`
 * (`{ run, flow, nodes }`), not list rows. Those entries have no top-level
 * `id`/`status`, so this guard skips them and the patch leaves the active
 * snapshot untouched.
 */
function isRunSummaryList(runs: readonly unknown[]): runs is FlowRunSummary[] {
  const first = runs[0];
  if (first === undefined) return true; // empty array: nothing to patch either way
  return (
    typeof (first as { id?: unknown }).id === 'string' &&
    typeof (first as { status?: unknown }).status === 'string'
  );
}

/** Statuses for which a run has finished and therefore has no active nodes. */
const TERMINAL_STATUSES = new Set<FlowRunSummary['status']>(['completed', 'error', 'cancelled']);

/** Field-wise equality of two summary rows (array fields compared by value). */
function summaryRowsEqual(a: FlowRunSummary, b: FlowRunSummary): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]) as Set<keyof FlowRunSummary>;
  for (const key of keys) {
    const av = a[key];
    const bv = b[key];
    if (Array.isArray(av) && Array.isArray(bv)) {
      if (av.length !== bv.length || av.some((value, i) => value !== bv[i])) return false;
    } else if (av !== bv) {
      return false;
    }
  }
  return true;
}

/**
 * Applies a `flow_run:updated` payload to a cached flow-runs list, keeping
 * each run's status (and the fields the list renders) in sync without a
 * refetch. The runs list is a flat history list: unlike the active-runs
 * snapshot, a run that goes terminal must STAY in the list, so this helper
 * never removes a row.
 *
 * Behavior:
 * - Absent run: returns the array unchanged. We never insert, so a per-flow
 *   list can't receive a run for a different flow.
 * - Present run: merges the partial payload OVER the existing row, so a
 *   payload that omits a field never blanks it.
 * - Terminal status: forces `currentNodeIds` to `[]`. The engine clears it in
 *   storage on completion/cancel/error but the terminal broadcast omits the
 *   field, so without this a finished row would keep a stale active-node count
 *   (no further `node_updated` events arrive to drain it).
 * - No effective change: returns the same array reference so React Query
 *   skips a re-render.
 * - Non-list cache entry (the active-runs snapshot): returns it unchanged
 *   (see {@link isRunSummaryList}).
 */
export function applyRunSummaryPatch(
  runs: FlowRunSummary[] | undefined,
  patch: Partial<FlowRunSummary> & { id: string },
): FlowRunSummary[] | undefined {
  if (!runs) return runs;
  if (!isRunSummaryList(runs)) return runs;
  const index = runs.findIndex((run) => run.id === patch.id);
  if (index === -1) return runs;

  const existing = runs[index];
  const merged: FlowRunSummary = { ...existing, ...patch };
  if (TERMINAL_STATUSES.has(merged.status)) merged.currentNodeIds = [];
  if (summaryRowsEqual(existing, merged)) return runs;
  const next = runs.slice();
  next[index] = merged;
  return next;
}

// ---------------------------------------------------------------------------
// InfiniteData dispatch
//
// The runs list is a `useInfiniteQuery`, so its cache entry is
// `InfiniteData<FlowRunSummaryPage>` (pages of rows), not a flat array. But the
// WS handlers patch with a single `['flow-runs', org]` prefix that ALSO matches
// the active-runs snapshot cache (`['flow-runs', org, 'active']`), which is a
// plain `ActiveFlowRunSnapshot[]`. These helpers dispatch on the cache shape:
// map the page array for infinite data, fall back to the flat-array helper for
// anything else (where the shape guard leaves the active snapshot untouched),
// and preserve referential identity when nothing changes so React Query skips
// the re-render.
// ---------------------------------------------------------------------------

function isInfiniteRunData(data: unknown): data is InfiniteData<FlowRunSummaryPage> {
  return (
    typeof data === 'object' && data !== null && Array.isArray((data as { pages?: unknown }).pages)
  );
}

/** Apply a per-page transform across an infinite cache, keeping refs stable. */
function mapRunListCache(
  data: unknown,
  fn: (runs: FlowRunSummary[]) => FlowRunSummary[] | undefined,
): unknown {
  if (isInfiniteRunData(data)) {
    let changed = false;
    const pages = data.pages.map((page) => {
      const nextRuns = fn(page.runs);
      if (nextRuns && nextRuns !== page.runs) {
        changed = true;
        return { ...page, runs: nextRuns };
      }
      return page;
    });
    return changed ? { ...data, pages } : data;
  }
  // Non-infinite entry (the active-runs snapshot): defer to the flat-array
  // helper, which returns the same reference for a shape it doesn't own.
  return fn(data as FlowRunSummary[]) ?? data;
}

/**
 * {@link applyRunSummaryPatch} lifted over an infinite runs cache. Use from the
 * `flow_run:updated` handler's `setQueriesData` updater.
 */
export function applyRunSummaryPatchToCache(
  data: unknown,
  patch: Partial<FlowRunSummary> & { id: string },
): unknown {
  return mapRunListCache(data, (runs) => applyRunSummaryPatch(runs, patch));
}

/**
 * {@link applyNodeUpdateToRunList} lifted over an infinite runs cache. Use from
 * the `flow_run:node_updated` handler's `setQueriesData` updater.
 */
export function applyNodeUpdateToCache(
  data: unknown,
  runId: string,
  node: Pick<FlowRunNode, 'nodeId' | 'status'>,
): unknown {
  return mapRunListCache(data, (runs) => applyNodeUpdateToRunList(runs, runId, node));
}
